import { getJson, postJson, type ApiError } from './client';
import { fetchUserToken } from './auth';
import {
  API_KEY_PREFIX,
  ensureApiKeyPrefix,
  getStoredSystemToken,
  setStoredSystemToken,
  setStoredApiKey,
  stripApiKeyPrefix,
} from '../utils/apiConfig';
import { getStoredUserId } from '../auth/userStorage';

export interface TokenListRequest {
  page?: number;
  size?: number;
}

export interface RawTokenListResponse {
  success?: boolean;
  message?: string;
  data?: unknown;
  [key: string]: unknown;
}

export interface TokenOption {
  value: string;
  label: string;
  raw: unknown;
}

const containerKeys = ['data', 'results', 'items', 'list', 'content', 'records', 'rows'];
const tokenKeys = ['token', 'apiKey', 'api_key', 'value', 'key', 'access_token', 'accessToken'];
const tokenNameKeys = [
  'name',
  'tokenName',
  'token_name',
  'tokenDisplayName',
  'display_name',
  'displayName',
  'label',
  'title',
  'alias',
  'remark',
  'description',
  'desc',
  'notes',
  'note',
];
const TOKEN_PATTERN = /^[A-Za-z0-9+/=_-]+$/;

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

export const isLikelyToken = (value: string): boolean => {
  const normalized = stripApiKeyPrefix(value);
  const trimmed = normalized.trim();
  if (!trimmed) {
    return false;
  }
  if (trimmed.length < 16 || trimmed.length > 256) {
    return false;
  }
  if (!TOKEN_PATTERN.test(trimmed)) {
    return false;
  }
  return true;
};

export const maskTokenLabel = (token: string): string => {
  const trimmed = token.trim();
  if (trimmed.length <= 8) {
    return trimmed;
  }
  const lower = trimmed.toLowerCase();
  const hasPrefix = lower.startsWith(API_KEY_PREFIX);
  const prefix = hasPrefix ? trimmed.slice(0, API_KEY_PREFIX.length) : '';
  const core = hasPrefix ? trimmed.slice(API_KEY_PREFIX.length) : trimmed;
  if (core.length <= 8) {
    return prefix ? `${prefix}${core}` : core;
  }
  const head = core.slice(0, 4);
  const tail = core.slice(-4);
  const maskedCore = `${head}...${tail}`;
  return prefix ? `${prefix}${maskedCore}` : maskedCore;
};

const readTokenName = (source: Record<string, unknown>): string | null => {
  for (const key of tokenNameKeys) {
    if (!(key in source)) {
      continue;
    }
    const candidate = source[key];
    if (typeof candidate === 'string') {
      const trimmed = candidate.trim();
      if (trimmed) {
        return trimmed;
      }
    }
  }
  return null;
};

const extractTokenOptions = (payload: unknown): TokenOption[] => {
  const discovered = new Map<string, TokenOption>();
  const visited = new WeakSet<object>();
  const queue: unknown[] = [payload];

  while (queue.length > 0) {
    const current = queue.shift();
    if (typeof current === 'string') {
      const token = current.trim();
      if (token && isLikelyToken(token)) {
        if (!discovered.has(token)) {
          discovered.set(token, {
            value: token,
            label: maskTokenLabel(token),
            raw: current,
          });
        }
      }
      continue;
    }
    if (Array.isArray(current)) {
      for (const item of current) {
        queue.push(item);
      }
      continue;
    }
    if (isPlainObject(current)) {
      if (visited.has(current)) {
        continue;
      }
      visited.add(current);

      const tokenCandidates: string[] = [];
      for (const key of tokenKeys) {
        if (!(key in current)) {
          continue;
        }
        const candidate = current[key];
        if (typeof candidate !== 'string') {
          continue;
        }
        const trimmed = candidate.trim();
        if (trimmed && isLikelyToken(trimmed)) {
          tokenCandidates.push(trimmed);
        }
      }

      if (tokenCandidates.length > 0) {
        const displayName = readTokenName(current);
        for (const token of tokenCandidates) {
          const existing = discovered.get(token);
          if (existing) {
            if (displayName && (!existing.label || existing.label === maskTokenLabel(existing.value))) {
              discovered.set(token, { ...existing, label: displayName, raw: current });
            }
            continue;
          }
          discovered.set(token, {
            value: token,
            label: displayName ?? maskTokenLabel(token),
            raw: current,
          });
        }
      }

      for (const key of containerKeys) {
        if (key in current) {
          queue.push(current[key]);
        }
      }

      // 兜底遍历对象的属性，避免遗漏嵌套结构中的数组
      for (const value of Object.values(current)) {
        if (value && (typeof value === 'object' || typeof value === 'string')) {
          queue.push(value);
        }
      }
    }
  }

  return Array.from(discovered.values());
};

export interface TokenListResult {
  options: TokenOption[];
  raw: RawTokenListResponse;
  resolvedToken: string;
}

const isUnauthorizedError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const status = (error as ApiError).status;
  if (typeof status === 'number' && (status === 401 || status === 403)) {
    return true;
  }
  const message =
    'message' in error && typeof (error as { message?: unknown }).message === 'string'
      ? (error as { message: string }).message.toLowerCase()
      : '';
  if (!message) {
    return false;
  }
  return message.includes('401') || message.includes('unauthorized') || message.includes('未登录') || message.includes('未授权') || message.includes('无权');
};

const shouldRetryFromPayload = (payload: RawTokenListResponse | null | undefined): boolean => {
  if (!payload) {
    return false;
  }
  const message = typeof payload.message === 'string' ? payload.message.toLowerCase() : '';
  if (!message) {
    return false;
  }
  return (
    message.includes('token') ||
    message.includes('未登录') ||
    message.includes('未授权') ||
    message.includes('无权') ||
    message.includes('过期') ||
    message.includes('失效') ||
    message.includes('expired') ||
    message.includes('invalid')
  );
};

const ensureStoredAccessToken = (): string | null => {
  const candidate = getStoredSystemToken().trim();
  if (!candidate) {
    return null;
  }
  return isLikelyToken(candidate) ? stripApiKeyPrefix(candidate) : null;
};

let refreshingAccessTokenPromise: Promise<string> | null = null;

const refreshStoredAccessToken = async (): Promise<string> => {
  if (refreshingAccessTokenPromise) {
    return refreshingAccessTokenPromise;
  }

  refreshingAccessTokenPromise = (async () => {
    const userId = getStoredUserId();
    if (!userId) {
      throw new Error('缺少用户 ID，无法刷新系统访问令牌');
    }
    const response = await fetchUserToken(userId);
    if (!response?.success || typeof response.data !== 'string' || !response.data.trim()) {
      const message =
        typeof response?.message === 'string' && response.message.trim()
          ? response.message.trim()
          : '系统访问令牌刷新失败，请重新登录后再试';
      throw new Error(message);
    }
    const rawToken = response.data.trim();
    const sanitized = ensureApiKeyPrefix(rawToken);
    if (!isLikelyToken(sanitized)) {
      throw new Error('系统访问令牌格式异常，请重新登录后再试');
    }
    setStoredSystemToken(rawToken);
    return stripApiKeyPrefix(sanitized);
  })();

  try {
    return await refreshingAccessTokenPromise;
  } finally {
    refreshingAccessTokenPromise = null;
  }
};

export const fetchTokenList = async (request: TokenListRequest = {}): Promise<TokenListResult> => {
  const page = Number.isFinite(request.page) ? Number(request.page) : 0;
  const size = Number.isFinite(request.size) ? Number(request.size) : 50;
  const params = new URLSearchParams();
  params.set('p', String(page));
  params.set('size', String(size));

  const userId = getStoredUserId();
  if (!userId) {
    throw new Error('缺少用户 ID，无法获取令牌列表');
  }

  const endpoint = `/api/workers-proxy/token-list?${params.toString()}`;
  const performRequest = async (token: string) => {
    const rawToken = stripApiKeyPrefix(token);
    if (!rawToken || !isLikelyToken(rawToken)) {
      throw new Error('系统访问令牌格式异常，请重新刷新后再试');
    }
    return getJson<RawTokenListResponse>(endpoint, {
      cache: 'no-store',
      includeAuth: false,
      resolveEndpoint: false,
      headers: {
        Authorization: rawToken,
        'new-api-user': userId,
      },
    });
  };

  let resolvedToken = ensureStoredAccessToken();
  if (!resolvedToken) {
    resolvedToken = await refreshStoredAccessToken();
  }

  let raw: RawTokenListResponse;
  let didRefresh = false;
  try {
    raw = await performRequest(resolvedToken);
  } catch (error) {
    if (!didRefresh && isUnauthorizedError(error)) {
      resolvedToken = await refreshStoredAccessToken();
      didRefresh = true;
      raw = await performRequest(resolvedToken);
    } else {
      throw error;
    }
  }

  if (raw && typeof raw.success === 'boolean' && raw.success === false) {
    if (!didRefresh && shouldRetryFromPayload(raw)) {
      resolvedToken = await refreshStoredAccessToken();
      didRefresh = true;
      raw = await performRequest(resolvedToken);
      if (raw && typeof raw.success === 'boolean' && raw.success === false) {
        const message =
          typeof raw.message === 'string' && raw.message.trim() ? raw.message.trim() : '令牌列表获取失败';
        throw new Error(message);
      }
    } else {
      const message =
        typeof raw.message === 'string' && raw.message.trim() ? raw.message.trim() : '令牌列表获取失败';
      throw new Error(message);
    }
  }

  const latestToken = stripApiKeyPrefix(resolvedToken);

  const discoveredOptions = extractTokenOptions(raw);
  const options: TokenOption[] = [];
  for (const option of discoveredOptions) {
    const rawValue = typeof option.value === 'string' ? option.value.trim() : '';
    if (!rawValue) {
      continue;
    }
    const baseToken = stripApiKeyPrefix(rawValue);
    if (!baseToken) {
      continue;
    }
    const prefixedToken = ensureApiKeyPrefix(baseToken);
    const label = option.label || maskTokenLabel(rawValue);
    options.push({
      value: rawValue,
      label,
      raw: {
        source: option.raw,
        token: baseToken,
        prefixedToken,
        name: label,
      },
    });
  }

  return {
    options,
    raw,
    resolvedToken: latestToken,
  };
};

// =============== Token creation + auto-config ===============

export interface CreateTokenRequestPayload {
  remain_quota: number;
  expired_time: number; // -1 永不过期
  unlimited_quota: boolean;
  model_limits_enabled: boolean;
  model_limits: string;
  group: string;
  mj_image_mode: string; // 'default'
  mj_custom_proxy: string;
  selected_groups: string[];
  name: string;
  allow_ips: string;
}

export interface CreateTokenResponse {
  success?: boolean;
  message?: string;
  data?: unknown;
  [key: string]: unknown;
}

export const createToken = async (payload: CreateTokenRequestPayload): Promise<CreateTokenResponse> => {
  const userId = getStoredUserId();
  if (!userId) {
    throw new Error('缺少用户 ID，无法创建令牌');
  }

  const getAccessToken = async (): Promise<string> => {
    let token = ensureStoredAccessToken();
    if (!token) token = await refreshStoredAccessToken();
    return token;
  };

  const resolvedToken = await getAccessToken();
  const endpoint = '/api/workers-proxy/token-create';

  const perform = async (accessToken: string) => {
    const rawToken = stripApiKeyPrefix(accessToken);
    if (!rawToken || !isLikelyToken(rawToken)) {
      throw new Error('系统访问令牌格式异常，请重新刷新后再试');
    }
    return postJson<CreateTokenResponse>(endpoint, payload, {
      includeAuth: false,
      resolveEndpoint: false,
      headers: {
        Authorization: rawToken,
        'new-api-user': userId,
      },
      cache: 'no-store',
    });
  };

  let result: CreateTokenResponse;
  let didRefresh = false;
  try {
    result = await perform(resolvedToken);
  } catch (error) {
    if (!didRefresh && isUnauthorizedError(error)) {
      const refreshed = await refreshStoredAccessToken();
      didRefresh = true;
      result = await perform(refreshed);
    } else {
      throw error;
    }
  }

  return result;
};

export interface AutoConfigResult {
  status: 'selected' | 'created' | 'skipped';
  apiKey?: string; // 已保存的 key（去前缀）
  message?: string;
}

const DEFAULT_TARGET_GROUP = '限时特价,default,优质gemini,逆向,纯AZ,官转gemini,官转';

export const autoConfigureApiKey = async (
  preferredGroup: string = DEFAULT_TARGET_GROUP
): Promise<AutoConfigResult> => {
  // 1) 读取列表并尝试匹配既有令牌
  const list = await fetchTokenList({ page: 0, size: 200 });
  const options = list?.options ?? [];
  const match = options.find((opt) => {
    const raw = opt?.raw as { source?: unknown } | undefined;
    const source = raw && typeof raw === 'object' ? (raw as any).source : undefined;
    const group: unknown = source && typeof source === 'object' ? (source as any).group : undefined;
    return typeof group === 'string' && group.trim() === preferredGroup;
  });

  if (match && match.value) {
    setStoredApiKey(match.value);
    return { status: 'selected', apiKey: stripApiKeyPrefix(match.value) };
  }

  // 2) 未命中则尝试创建
  const name = `auto-${Date.now()}`;
  const groups = preferredGroup.split(',').map((s) => s.trim()).filter(Boolean);

  const createPayload: CreateTokenRequestPayload = {
    remain_quota: 250000000000,
    expired_time: -1,
    unlimited_quota: true,
    model_limits_enabled: false,
    model_limits: '',
    group: preferredGroup,
    mj_image_mode: 'default',
    mj_custom_proxy: '',
    selected_groups: groups,
    name,
    allow_ips: '',
  };

  const created = await createToken(createPayload);
  if (!created || (typeof created.success === 'boolean' && !created.success)) {
    const msg = (created && typeof created.message === 'string' && created.message.trim())
      ? created.message.trim()
      : '创建令牌失败';
    throw new Error(msg);
  }

  // 3) 重新拉取并保存
  const after = await fetchTokenList({ page: 0, size: 200 });
  const createdMatch = (after?.options ?? []).find((opt) => {
    const raw = opt?.raw as { source?: unknown } | undefined;
    const source = raw && typeof raw === 'object' ? (raw as any).source : undefined;
    const group: unknown = source && typeof source === 'object' ? (source as any).group : undefined;
    return typeof group === 'string' && group.trim() === preferredGroup;
  });

  if (createdMatch && createdMatch.value) {
    setStoredApiKey(createdMatch.value);
    return { status: 'created', apiKey: stripApiKeyPrefix(createdMatch.value) };
  }

  return { status: 'skipped', message: '未找到或创建匹配的令牌' };
};
